import { useState } from 'react'
import type { ProofOfAcceptanceEventFormValues } from '@klear/proof-of-acceptance/wire.client'
import { trpc, type RouterInputs } from '@klear/ui/lib/trpc'
import { ProofOfAcceptanceForm } from '@klear/order-event-ui/event-forms/proof-of-acceptance-form'
import {
  proofOfAcceptanceFormToCreateWire,
  proofOfAcceptanceFormToUpdateWire
} from '@klear/proof-of-acceptance/wire.client'
import { EventFormShell, type EventFormHandlerProps } from '@klear/order-event-ui/event-shared/event-form-shell'
import { useEventFormState } from '@klear/order-event-ui/event-shared/event-form-state'
import { useEventFormSave, type RunEventCommit } from '@klear/order-event-ui/event-shared/use-event-form-save'

export function ProofOfAcceptanceEventForm({
  bootstrap,
  orderId,
  traderId,
  onSuccess,
  onCancel
}: EventFormHandlerProps) {
  const formState = useEventFormState<ProofOfAcceptanceEventFormValues>({
    initial: bootstrap.formValues as ProofOfAcceptanceEventFormValues
  })
  const [isLoading, setIsLoading] = useState(false)
  const { save } = useEventFormSave()
  const commit = trpc.proofOfAcceptance.commit.useMutation()
  const parentEventId = bootstrap.parentEventId

  const handleSubmit = async (data: ProofOfAcceptanceEventFormValues) => {
    const files = (data.files ?? []).filter((f): f is File => f instanceof File)
    const keptDocumentIds = (data.documents ?? []).map((document) => document.id)
    const isUpdate = bootstrap.editEventId != null

    if (!isUpdate && files.length === 0) {
      formState.setFieldError('files', 'At least one file is required')
      return
    }
    if (isUpdate && files.length === 0 && keptDocumentIds.length === 0) {
      formState.setFieldError('files', 'At least one file is required')
      return
    }

    setIsLoading(true)
    try {
      const runCommit: RunEventCommit = ({ id }) => {
        const input = (
          id == null
            ? {
                traderId,
                data: proofOfAcceptanceFormToCreateWire(data, orderId, parentEventId)
              }
            : {
                id,
                traderId,
                data: proofOfAcceptanceFormToUpdateWire(data, parentEventId)
              }
        ) as RouterInputs['proofOfAcceptance']['commit']
        return commit.mutateAsync(input)
      }
      await save({
        traderId,
        eventId: bootstrap.editEventId,
        keptDocumentIds,
        originalDocumentIds: bootstrap.originalDocumentIds,
        files,
        runCommit
      })
      onSuccess?.()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EventFormShell
      formState={formState}
      title={`${bootstrap.editEventId != null ? 'Edit' : 'Add'} Proof of Acceptance`}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      <ProofOfAcceptanceForm />
    </EventFormShell>
  )
}
